import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import type { DataAsset } from '@/types'

interface Comment {
  id: string
  userId: string
  userName: string
  content: string
  createdAt: string
}

// 模拟评论数据 - 使用经验、数据质量相关
const MOCK_COMMENTS: Record<string, Comment[]> = {
  // 风险资产分类统计表
  '1': [
    { id: '1', userId: 'u1', userName: '张三', content: '数据更新很及时，每天都能及时看到最新的五级分类情况，对风险管理很有帮助！', createdAt: '2024-01-15 10:30' },
    { id: '2', userId: 'u2', userName: '李四', content: '数据质量不错，完整性和准确性都很高。建议增加筛选功能，方便按机构查看。', createdAt: '2024-01-16 14:20' },
    { id: '3', userId: 'u3', userName: '王五', content: '这个表对我们做风险监测很有用，五级分类口径清晰，值得推荐！', createdAt: '2024-01-17 09:15' },
  ],
  // 零售客户信息表
  '2': [
    { id: '4', userId: 'u4', userName: '赵六', content: '客户信息很完整，证件号码、手机号都有，数据准确性也不错。', createdAt: '2024-01-14 09:15' },
    { id: '5', userId: 'u5', userName: '钱七', content: '使用体验很好，数据更新及时。不过部分历史客户信息有缺失，希望能补充完整。', createdAt: '2024-01-15 11:30' },
  ],
  // 对公存款日报
  '3': [
    { id: '6', userId: 'u6', userName: '孙八', content: '日报数据很有用，可以及时了解对公存款变动情况。数据质量很好！', createdAt: '2024-01-13 08:45' },
  ],
  // 授信业务明细表
  '4': [
    { id: '7', userId: 'u7', userName: '周九', content: '授信业务数据很详细，额度、用信、担保信息都有，做分析很方便。', createdAt: '2024-01-12 14:20' },
    { id: '8', userId: 'u8', userName: '吴十', content: '数据口径清晰，完整性高。建议增加一些常用的统计指标，如授信利用率等。', createdAt: '2024-01-14 16:30' },
  ],
  // 金融市场交易流水
  '5': [
    { id: '9', userId: 'u9', userName: '郑一', content: '交易流水记录很完整，债券、回购、同业业务都有，数据分析很方便！', createdAt: '2024-01-11 10:00' },
  ],
  // 监管报送指标表
  '6': [
    { id: '10', userId: 'u10', userName: '冯二', content: '监管报送指标汇总得很全面，1104、EAST、一表通都有，使用很方便。', createdAt: '2024-01-10 15:30' },
    { id: '11', userId: 'u11', userName: '褚三', content: '数据质量很高，校验也很严格。建议增加一些数据质量监控指标。', createdAt: '2024-01-12 09:45' },
  ],
}

interface CommentsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  asset: DataAsset | null
}

export function CommentsModal({ open, onOpenChange, asset }: CommentsModalProps) {
  const [newComment, setNewComment] = useState('')
  const [comments, setComments] = useState<Comment[]>([])

  // 加载评论
  useEffect(() => {
    if (asset) {
      setComments(MOCK_COMMENTS[asset.id] || [])
    }
  }, [asset])

  // 重置评论输入
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setNewComment('')
    }
    onOpenChange(newOpen)
  }

  const handleSubmit = () => {
    if (newComment.trim() && asset) {
      const comment: Comment = {
        id: Date.now().toString(),
        userId: 'current',
        userName: '我',
        content: newComment.trim(),
        createdAt: new Date().toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        }).replace(/\//g, '-'),
      }
      setComments([comment, ...comments])
      setNewComment('')
    }
  }

  if (!asset) return null

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>评论 - {asset.name}</DialogTitle>
        </DialogHeader>

        {/* 评论列表 */}
        <ScrollArea className="flex-1 max-h-[60vh] pr-4 -mr-4">
          <div className="space-y-4 py-2">
            {comments.length === 0 ? (
              <p className="text-center text-sm text-gray-500 py-8">暂无评论，快来抢沙发吧~</p>
            ) : (
              comments.map((comment) => (
                <div key={comment.id} className="flex gap-3">
                  <Avatar className="w-8 h-8">
                    <AvatarFallback className="text-xs bg-gcb-primary text-white">
                      {comment.userName[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">{comment.userName}</span>
                      <span className="text-xs text-gray-500">{comment.createdAt}</span>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed">{comment.content}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>

        {/* 评论输入框 */}
        <div className="flex gap-2 pt-2 border-t">
          <Input
            placeholder="写下你的评论..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                handleSubmit()
              }
            }}
            className="flex-1"
          />
          <Button
            onClick={handleSubmit}
            disabled={!newComment.trim()}
            className="px-4"
          >
            发送
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
